import React, { useState, useEffect } from 'react';
import { supabase } from '../supabase';

const MOCK_OPERATION = {
  id: 'OP-2025-041',
  type: 'Corn Planting',
  field: 'North Quarter',
  fieldId: 'F-001',
  acres: 156,
  completed: 135.7,
  status: 'active',
  priority: 'high',
  equipment: '8R 410 + 1795 Planter',
  equipmentId: 'JD-8R-410',
  operator: 'Jake Morrison',
  operatorId: 'C-001',
  date: '2025-02-07',
  startTime: '6:00 AM',
  product: 'DKC 62-08 Corn',
  rate: '34,000 seeds/ac',
  depth: '2.0 inches',
  rowSpacing: '30 inches',
  speed: '5.2 mph',
  notes: 'Variable rate prescription loaded. Increase population in zones 3 and 4.',
};

const MOCK_SOP_CHECKLIST = [
  { id: 1, category: 'Pre-Operation', task: 'Review field prescription map', done: true },
  { id: 2, category: 'Pre-Operation', task: 'Verify seed variety matches work order', done: true },
  { id: 3, category: 'Pre-Operation', task: 'Check planter calibration - population test', done: true },
  { id: 4, category: 'Pre-Operation', task: 'Inspect seed tubes and closing wheels', done: true },
  { id: 5, category: 'Pre-Operation', task: 'Verify GPS guidance lines loaded', done: true },
  { id: 6, category: 'Pre-Operation', task: 'Check hydraulic connections and pressure', done: true },
  { id: 7, category: 'Pre-Operation', task: 'Fill seed hoppers - record lot numbers', done: true },
  { id: 8, category: 'In-Field', task: 'Confirm planting depth at 2.0 inches (first 50ft)', done: true },
  { id: 9, category: 'In-Field', task: 'Check seed-to-soil contact (dig check)', done: true },
  { id: 10, category: 'In-Field', task: 'Verify population monitor readings match target', done: true },
  { id: 11, category: 'In-Field', task: 'Monitor row unit downforce', done: false },
  { id: 12, category: 'In-Field', task: 'Check for skips and doubles every 30 acres', done: false },
  { id: 13, category: 'In-Field', task: 'Refill seed at headland - record quantities', done: false },
  { id: 14, category: 'Post-Operation', task: 'Record final acres and seed usage', done: false },
  { id: 15, category: 'Post-Operation', task: 'Upload as-planted map to JD Ops Center', done: false },
  { id: 16, category: 'Post-Operation', task: 'Clean planter and inspect for damage', done: false },
  { id: 17, category: 'Post-Operation', task: 'Supervisor sign-off', done: false },
];

const MOCK_LOG = [
  { time: '6:00 AM', event: 'Operation started - Jake Morrison clocked in' },
  { time: '6:15 AM', event: 'Pre-operation checklist completed' },
  { time: '6:22 AM', event: 'Planting started - Row 1, Pass 1' },
  { time: '8:45 AM', event: 'Seed refill - 12 units DKC 62-08 added' },
  { time: '10:30 AM', event: 'Dig check performed - depth 1.9-2.1 inches OK' },
  { time: '11:15 AM', event: '87% field coverage reached' },
  { time: '12:00 PM', event: 'Lunch break - operation paused 30 min' },
  { time: '12:30 PM', event: 'Operation resumed' },
];

function OperationDetailPage() {
  const [operation] = useState(MOCK_OPERATION);
  const [checklist, setChecklist] = useState(MOCK_SOP_CHECKLIST);
  const [log] = useState(MOCK_LOG);

  useEffect(() => { /* Future: fetch from Supabase by operation ID */ }, []);

  const toggleCheck = (id) => {
    setChecklist(prev => prev.map(item => item.id === id ? { ...item, done: !item.done } : item));
  };

  const getStatusBadge = (status) => {
    const map = { active: 'badge-success', completed: 'badge-default', scheduled: 'badge-warning', paused: 'badge-danger' };
    return map[status] || 'badge-default';
  };

  const completedTasks = checklist.filter(c => c.done).length;
  const progressPct = Math.round((operation.completed / operation.acres) * 100);
  const categories = [...new Set(checklist.map(c => c.category))];

  return (
    <div className="page-body">
      <div className="page-header">
        <div>
          <div style={{ fontSize: "0.85rem", opacity: 0.6, marginBottom: "4px" }}>
            <span style={{ cursor: 'pointer', textDecoration: 'underline' }} onClick={() => window.history.back()}>Operations</span> / {operation.id}
          </div>
          <h1 style={{ margin: 0 }}>{operation.type} &mdash; {operation.field}</h1>
          <p style={{ margin: '4px 0 0', opacity: 0.7, fontSize: '0.85rem' }}>{operation.id} &bull; {operation.date} &bull; Started {operation.startTime}</p>
        </div>
        <span className={getStatusBadge(operation.status)} style={{ fontSize: '0.9rem', padding: '6px 14px' }}>{operation.status}</span>
      </div>

      {/* Progress and Details */}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "16px" }}>
        <div className="card">
          <h3 style={{ margin: '0 0 12px' }}>Operation Progress</h3>
          <div style={{ textAlign: "center", marginBottom: "16px" }}>
            <div style={{ fontSize: "3rem", fontWeight: 700, color: "#367C2B" }}>{progressPct}%</div>
            <div style={{ fontSize: '0.9rem', opacity: 0.7 }}>{operation.completed} of {operation.acres} acres</div>
          </div>
          <div style={{ width: "100%", height: "10px", background: "rgba(255,255,255,0.1)", borderRadius: "5px", marginBottom: "16px" }}>
            <div style={{ width: progressPct + "%", height: "100%", borderRadius: "5px", background: "#367C2B", transition: "width 0.3s" }} />
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "8px", fontSize: "0.85rem" }}>
            <div style={{ opacity: 0.6 }}>Speed</div><div>{operation.speed}</div>
            <div style={{ opacity: 0.6 }}>Product</div><div>{operation.product}</div>
            <div style={{ opacity: 0.6 }}>Rate</div><div>{operation.rate}</div>
            <div style={{ opacity: 0.6 }}>Depth</div><div>{operation.depth}</div>
            <div style={{ opacity: 0.6 }}>Row Spacing</div><div>{operation.rowSpacing}</div>
            <div style={{ opacity: 0.6 }}>Priority</div><div style={{ color: '#D9534F', fontWeight: 600, textTransform: 'capitalize' }}>{operation.priority}</div>
          </div>
        </div>

        <div className="card">
          <h3 style={{ margin: '0 0 12px' }}>Assignment Details</h3>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "8px", fontSize: "0.85rem" }}>
            <div style={{ opacity: 0.6 }}>Operator</div><div style={{ fontWeight: 600 }}>{operation.operator}</div>
            <div style={{ opacity: 0.6 }}>Operator ID</div><div style={{ fontFamily: 'monospace' }}>{operation.operatorId}</div>
            <div style={{ opacity: 0.6 }}>Equipment</div><div style={{ fontWeight: 600 }}>{operation.equipment}</div>
            <div style={{ opacity: 0.6 }}>Equipment ID</div><div style={{ fontFamily: 'monospace' }}>{operation.equipmentId}</div>
            <div style={{ opacity: 0.6 }}>Field</div><div style={{ fontWeight: 600 }}>{operation.field}</div>
            <div style={{ opacity: 0.6 }}>Field ID</div><div style={{ fontFamily: 'monospace' }}>{operation.fieldId}</div>
          </div>
          {operation.notes && (
            <div style={{ marginTop: "12px", padding: "10px", background: "rgba(255,222,0,0.08)", borderLeft: "4px solid #FFDE00", borderRadius: "4px", fontSize: "0.85rem" }}>
              <strong>Notes:</strong> {operation.notes}
            </div>
          )}
        </div>
      </div>

      {/* SOP Checklist */}
      <div className="card" style={{ marginTop: "16px" }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
          <h3 style={{ margin: 0 }}>SOP Checklist</h3>
          <span style={{ fontSize: '0.85rem', opacity: 0.7 }}>{completedTasks} of {checklist.length} completed</span>
        </div>
        <div style={{ width: "100%", height: "6px", background: "rgba(255,255,255,0.1)", borderRadius: "3px", marginBottom: "16px" }}>
          <div style={{ width: Math.round((completedTasks / checklist.length) * 100) + "%", height: "100%", borderRadius: "3px", background: "#367C2B" }} />
        </div>
        {categories.map(cat => (
          <div key={cat} style={{ marginBottom: "16px" }}>
            <div style={{ fontSize: "0.8rem", fontWeight: 600, textTransform: "uppercase", opacity: 0.5, marginBottom: "8px", letterSpacing: "0.5px" }}>{cat}</div>
            {checklist.filter(c => c.category === cat).map(item => (
              <div key={item.id} onClick={() => toggleCheck(item.id)} style={{ display: "flex", alignItems: "center", gap: "10px", padding: "8px 10px", borderRadius: "6px", cursor: "pointer", marginBottom: "4px", background: item.done ? "rgba(54,124,43,0.08)" : "transparent" }}>
                <div style={{ width: "20px", height: "20px", borderRadius: "4px", border: item.done ? "2px solid #367C2B" : "2px solid rgba(255,255,255,0.2)", background: item.done ? "#367C2B" : "transparent", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, fontSize: "12px", color: "#fff" }}>
                  {item.done && "✓"}
                </div>
                <span style={{ fontSize: '0.9rem', textDecoration: item.done ? 'line-through' : 'none', opacity: item.done ? 0.6 : 1 }}>{item.task}</span>
              </div>
            ))}
          </div>
        ))}
      </div>

      {/* Activity Log */}
      <div className="card" style={{ marginTop: "16px" }}>
        <h3 style={{ margin: '0 0 12px' }}>Activity Log</h3>
        <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
          {log.map((entry, idx) => (
            <div key={idx} style={{ display: "flex", gap: "12px", padding: "8px 0", borderBottom: "1px solid rgba(255,255,255,0.05)" }}>
              <div style={{ fontSize: "0.8rem", fontFamily: "monospace", opacity: 0.5, whiteSpace: "nowrap", minWidth: "70px" }}>{entry.time}</div>
              <div style={{ fontSize: '0.85rem' }}>{entry.event}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

export default OperationDetailPage;